import React from 'react';

import { useAppFieldContext } from '@/app/lib/tanstack-form';
import { FieldInfo } from '@/common/components/field-info';
import { Description, Field, Label } from '@/common/components/fieldset';
import { Textarea } from '@/common/components/textarea';

export type FormTextAreaProps = {
  /** The label content for the textarea field. Can be a string or JSX element. */
  label: React.ReactNode;
  /** Optional description text shown below the label to provide additional context. */
  description?: React.ReactNode;
  /** Placeholder text shown when the textarea is empty. */
  placeholder?: string;
  /** Additional CSS classes to apply to the textarea element. */
  className?: string;
  /** Whether the textarea should be resizable. Defaults to true. */
  resizable?: boolean;
  /** Number of visible text lines for the textarea. */
  rows?: number;
};

/**
 * A form textarea component that integrates with TanStack Form using useFieldContext.
 *
 * This component provides a complete form field with label, optional description,
 * textarea control, and automatic error display. It uses Catalyst UI components for
 * consistent styling and accessibility.
 *
 * @example
 * ```tsx
 * <form.Field name="description">
 *   <FormTextArea
 *     label="Description"
 *     description="Provide a detailed description"
 *     placeholder="Enter description here..."
 *     rows={4}
 *   />
 * </form.Field>
 * ```
 */
export function FormTextArea({
  label,
  description,
  placeholder,
  className,
  resizable = true,
  rows
}: FormTextAreaProps) {
  const field = useAppFieldContext<string>();

  return (
    <Field>
      <Label htmlFor={field.name}>{label}</Label>
      {description && <Description>{description}</Description>}
      <Textarea
        id={field.name}
        name={field.name}
        value={field.state.value}
        placeholder={placeholder}
        rows={rows}
        resizable={resizable}
        onBlur={field.handleBlur}
        onChange={event => field.handleChange(event.target.value)}
        invalid={field.state.meta.isTouched && !field.state.meta.isValid}
        className={className}
      />
      <FieldInfo field={field} />
    </Field>
  );
}
